<script setup>
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { STATUS } from 'dashboard/store/constants';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  metric: {
    type: Object,
    default: () => ({}),
  },
  accountSummaryKey: {
    type: String,
    default: 'getAccountSummary',
  },
  summaryFetchingKey: {
    type: String,
    default: 'getAccountSummaryFetchingStatus',
  },
});

const { t } = useI18n();

const { calculateTrend, displayMetric, isAverageMetricType, fetchingStatus } =
  useReportMetrics(props.accountSummaryKey, props.summaryFetchingKey);

const trendColor = (value, key) => {
  if (isAverageMetricType(key)) {
    return value > 0
      ? 'border-n-ruby-9 text-n-ruby-9'
      : 'border-n-teal-10 text-n-teal-10';
  }
  return value < 0
    ? 'border-n-ruby-9 text-n-ruby-9'
    : 'border-n-teal-10 text-n-teal-10';
};

const getMetricDisplay = key => displayMetric(key);

const getAverageMetricParts = key => {
  const metricDisplay = getMetricDisplay(key);
  const matches = [...metricDisplay.matchAll(/(\d+)([^\d]+)/g)];

  if (!matches.length) {
    return [{ value: metricDisplay, unit: '' }];
  }

  return matches.map(([, value, unit]) => ({
    value,
    unit,
  }));
};
</script>

<template>
  <div class="text-n-slate-11">
    <span class="text-sm">
      {{ metric.NAME }}
    </span>
    <div class="flex items-end text-n-slate-12">
      <div v-if="fetchingStatus === STATUS.FETCHING">
        <Spinner />
      </div>
      <div
        v-else-if="fetchingStatus === STATUS.FAILED"
        class="text-n-ruby-10 text-sm"
      >
        {{ t('REPORT.SUMMARY_FETCHING_FAILED') }}
      </div>
      <div
        v-else-if="fetchingStatus === STATUS.FINISHED"
        class="flex items-baseline gap-1 text-xl font-medium leading-none"
      >
        <template v-if="isAverageMetricType(metric.KEY)">
          <span
            v-for="(part, index) in getAverageMetricParts(metric.KEY)"
            :key="`${metric.KEY}-${index}-${part.unit}`"
            class="inline-flex items-baseline leading-none"
          >
            <span class="leading-none">{{ part.value }}</span>
            <span
              v-if="part.unit"
              class="ml-0.5 text-sm leading-none text-n-slate-11"
            >
              {{ part.unit }}
            </span>
          </span>
        </template>
        <template v-else>
          {{ getMetricDisplay(metric.KEY) }}
        </template>
      </div>
      <div
        v-if="metric.trend && fetchingStatus === STATUS.FINISHED"
        class="text-xs ml-4 flex items-center mb-0.5"
      >
        <div
          v-if="metric.trend < 0"
          class="h-0 w-0 border-x-4 medium border-x-transparent border-t-[8px] mr-1"
          :class="trendColor(metric.trend, metric.KEY)"
        />
        <div
          v-else
          class="h-0 w-0 border-x-4 medium border-x-transparent border-b-[8px] mr-1"
          :class="trendColor(metric.trend, metric.KEY)"
        />
        <span class="font-medium" :class="trendColor(metric.trend, metric.KEY)">
          {{ calculateTrend(metric.KEY) }}%
        </span>
      </div>
    </div>
  </div>
</template>
